<template>
    <div
        class="team-banner"
        :class="{ 'team-banner--compact': compact, 'team-banner--mobile': $vuetify.breakpoint.xs }"
        :style="{ '--team-color': team.color || '#ff7133' }"
    >
        <div class="team-banner__inner">
            <div class="team-banner__logo">
                <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt="">
                <v-icon v-else class="team-banner__logo-icon">mdi-basketball</v-icon>
            </div>

            <div class="team-banner__info">
                <div class="team-banner__labels">
                    <span v-if="leagueTitle" class="team-banner__league">{{ leagueTitle }}</span>
                    <span v-if="isMyTeam" class="team-banner__mine custom-orange">Η ομάδα μου</span>
                </div>
                <h1 class="team-banner__name">{{ team.name }}</h1>

                <div class="team-banner__stats">
                    <div v-if="rankingRow" class="team-banner__stat team-banner__stat--position">
                        <span class="team-banner__stat-value">{{ rankingRow.position }}<small>η</small></span>
                        <span class="team-banner__stat-label">θέση / {{ rankingSize }}</span>
                    </div>
                    <div class="team-banner__stat">
                        <span class="team-banner__stat-value">{{ wins }}</span>
                        <span class="team-banner__stat-label">Νίκες</span>
                    </div>
                    <div class="team-banner__stat">
                        <span class="team-banner__stat-value">{{ losses }}</span>
                        <span class="team-banner__stat-label">Ήττες</span>
                    </div>
                    <div v-if="!compact && wins + losses > 0" class="team-banner__stat">
                        <span class="team-banner__stat-value">{{ winPercentage }}<small>%</small></span>
                        <span class="team-banner__stat-label">Νίκες %</span>
                    </div>
                    <div v-if="!compact && form.length" class="team-banner__form">
                        <span class="team-banner__stat-label">Φόρμα</span>
                        <span class="team-banner__form-letters">
                            <span
                                v-for="(result, index) in form"
                                :key="index"
                                class="team-banner__form-letter"
                                :class="result === 'W' ? 'is-win' : 'is-loss'"
                            >{{ result }}</span>
                        </span>
                    </div>
                </div>
            </div>

            <div class="team-banner__actions">
                <match-type-choice :compact="compact" :current-type="type" :counts="phaseCounts" @setType="setType" />
                <div v-if="!compact && team.id" class="team-banner__buttons">
                    <team-calendar-button :team-id="team.id" />
                    <v-btn
                        small
                        depressed
                        :color="isMyTeam ? 'orange' : 'white'"
                        :dark="isMyTeam"
                        class="team-banner__my-team"
                        @click="toggleMyTeam"
                    >
                        <v-icon small left>{{ isMyTeam ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                        {{ isMyTeam ? 'Η ομάδα μου' : 'Ορισμός ως ομάδα μου' }}
                    </v-btn>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import MatchTypeChoice from '@/components/MatchTypeChoice'
    import TeamCalendarButton from '@/components/TeamCalendarButton'
    import { prepareRanking, getLastFiveForm, getTeamId } from '@/utils/standings'

    export default {
        components: { MatchTypeChoice, TeamCalendarButton },
        props: {
            team: {
                type: Object,
                default: () => ({})
            },
            type: {
                type: String,
                default: 'regular_season'
            },
            compact: {
                type: Boolean,
                default: false
            },
            ranking: {
                type: Array,
                default: () => []
            },
            phaseCounts: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            leagueTitle() {
                return this.team?.league?.title || this.team?.leagueTitle || ''
            },
            preparedRanking() {
                return prepareRanking(this.ranking)
            },
            rankingRow() {
                if (!this.team.id || this.type === 'cup') {
                    return null
                }
                return this.preparedRanking.find(row => String(getTeamId(row)) === String(this.team.id)) || null
            },
            rankingSize() {
                return this.preparedRanking.length
            },
            wins() {
                return Number(this.team.seasonWins) || 0
            },
            losses() {
                return Number(this.team.seasonLoses) || 0
            },
            winPercentage() {
                const games = this.wins + this.losses
                return games ? Math.round(this.wins / games * 100) : 0
            },
            form() {
                return this.rankingRow ? getLastFiveForm(this.rankingRow) : []
            },
            isMyTeam() {
                const mine = this.$store.getters.getTeam
                return !!(mine && this.team.id && String(mine.id) === String(this.team.id))
            }
        },
        methods: {
            setType(option) {
                this.$emit('setType', option)
            },
            toggleMyTeam() {
                if (this.isMyTeam) {
                    this.$store.dispatch('setPlayer', null)
                    this.$store.dispatch('setTeam', null)
                    return
                }
                // Only what the app reads back (id, logo); the full team with every player's stats is too big for localStorage
                const { id, name, logo, color } = this.team
                this.$store.dispatch('setPlayer', null)
                this.$store.dispatch('setTeam', { id, name, logo, color })
            }
        }
    }
</script>

<style scoped>
.team-banner {
    padding: 0 12px;
    font-family: Ubuntu, Arial, sans-serif;
}

.team-banner__inner {
    display: flex;
    align-items: center;
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px 24px;
    border-radius: 14px;
    background: linear-gradient(110deg, var(--team-color) 20%, #fff 20%);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
    text-align: left;
    transition: padding 0.25s ease;
}

.team-banner__logo {
    display: flex;
    flex: 0 0 140px;
    align-items: center;
    justify-content: center;
    width: 140px;
    height: 140px;
    padding: 12px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    transition: all 0.25s ease;
}

.team-banner__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.team-banner__logo-icon {
    font-size: 80px !important;
    color: #ff7133 !important;
}

.team-banner__info {
    flex: 1 1 auto;
    min-width: 0;
}

.team-banner__labels {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.team-banner__league,
.team-banner__mine {
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.team-banner__league {
    background: #0d0d0f;
    color: #fff;
}

.team-banner__mine {
    color: #fff;
}

.team-banner__name {
    margin-top: 4px;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 2.3rem;
    font-weight: 800;
    line-height: 1.1;
    word-break: break-word;
}

.team-banner__stats {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
    margin-top: 12px;
}

.team-banner__stat,
.team-banner__form {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 64px;
    padding: 6px 10px;
    border-radius: 8px;
    background: #f4f4f6;
}

.team-banner__stat--position {
    background: #0d0d0f;
    color: #fff;
}

.team-banner__stat-value {
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.team-banner__stat-value small {
    font-size: 0.7rem;
    margin-left: 1px;
}

.team-banner__stat-label {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.team-banner__stat--position .team-banner__stat-label {
    color: rgba(255, 255, 255, 0.7);
}

.team-banner__form-letters {
    display: inline-flex;
    gap: 3px;
    margin-top: 3px;
}

.team-banner__form-letter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 800;
}

.team-banner__form-letter.is-win {
    background: #2e7d32;
}

.team-banner__form-letter.is-loss {
    background: #c62828;
}

.team-banner__actions {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    max-width: 340px;
}

.team-banner__buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
}

.team-banner--mobile .team-banner__buttons {
    justify-content: center;
}

.team-banner__my-team {
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}

/* ---------- Compact (sticky header while scrolling, desktop) ---------- */
.team-banner--compact .team-banner__inner {
    gap: 16px;
    padding: 8px 16px;
    margin-top: 0;
    background: linear-gradient(110deg, var(--team-color) 9%, #fff 9%);
}

.team-banner--compact .team-banner__logo {
    flex-basis: 56px;
    width: 56px;
    height: 56px;
    padding: 5px;
}

.team-banner--compact .team-banner__logo-icon {
    font-size: 36px !important;
}

.team-banner--compact .team-banner__labels {
    display: none;
}

.team-banner--compact .team-banner__name {
    margin-top: 0;
    font-size: 1.3rem;
}

.team-banner--compact .team-banner__stats {
    margin-top: 4px;
    gap: 6px;
}

.team-banner--compact .team-banner__stat {
    flex-direction: row;
    gap: 4px;
    min-width: 0;
    padding: 2px 8px;
}

.team-banner--compact .team-banner__stat-value {
    font-size: 0.95rem;
}

/* ---------- Mobile ---------- */
.team-banner--mobile .team-banner__inner {
    flex-direction: column;
    gap: 12px;
    padding: 18px 14px;
    text-align: center;
    background: linear-gradient(180deg, var(--team-color) 26%, #fff 26%);
}

.team-banner--mobile .team-banner__logo {
    flex-basis: auto;
    width: 110px;
    height: 110px;
}

.team-banner--mobile .team-banner__labels,
.team-banner--mobile .team-banner__stats {
    justify-content: center;
}

.team-banner--mobile .team-banner__name {
    font-size: 1.7rem;
}

.team-banner--mobile .team-banner__actions {
    align-items: center;
    max-width: none;
    width: 100%;
}
</style>
